import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import {
  LuArrowLeft, LuCopy, LuCheck, LuExternalLink, LuWebhook,
  LuTrash2, LuCircleCheck, LuCircleAlert, LuArrowRight, LuCode,
  LuZap, LuCloud, LuTerminal, LuShield, LuTriangleAlert, LuRefreshCw
} from "react-icons/lu";
import { FaWhatsapp } from "react-icons/fa";
import { API } from "../App";

const emptyForm = { name: "", phone_number_id: "", access_token: "", waba_id: "" };

const WhatsAppSetupPage = ({ auth }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null); // config being edited
  const [copied, setCopied] = useState({});
  const [activeTab, setActiveTab] = useState("webhook");
  const [formData, setFormData] = useState(emptyForm);

  // Keep legacy single-config reference for tabs/webhook info display
  const config = configs[0] || null;
  const webhookUrl = config?.webhook_url || "(Añade un número primero)";
  const verifyToken = "botnexus_verify";
  const incomingUrl = config?.webhook_url || "(Añade un número primero)";

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try {
      const response = await fetch(`${API}/whatsapp/configs`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) setConfigs(await response.json());
    } catch (error) {
      console.error("Error fetching configs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isEdit = !!editingConfig;
      const url = isEdit ? `${API}/whatsapp/config/${editingConfig.id}` : `${API}/whatsapp/config`;
      const method = isEdit ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success(isEdit ? "Número actualizado correctamente" : "¡Número añadido correctamente!");
        setAddDialogOpen(false);
        setEditingConfig(null);
        setFormData(emptyForm);
        fetchConfigs();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al verificar credenciales");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm("¿Eliminar este número de WhatsApp?")) return;
    try {
      const response = await fetch(`${API}/whatsapp/config/${configId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        setConfigs(prev => prev.filter(c => c.id !== configId));
        toast.success("Número eliminado");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openEdit = (c) => {
    setEditingConfig(c);
    setFormData({ name: c.name || "", phone_number_id: c.phone_number_id, access_token: "", waba_id: c.waba_id || "" });
    setAddDialogOpen(true);
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [field]: true });
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopied({ ...copied, [field]: false }), 2000);
    } catch (error) {
      toast.error("Error al copiar");
    }
  };

  const CopyField = ({ label, value, fieldKey, mono = true }) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          readOnly
          className={`bg-muted/50 border-transparent text-sm ${mono ? 'font-mono' : ''}`}
        />
        <Button
          variant="outline"
          size="icon"
          className="shrink-0"
          onClick={() => copyToClipboard(value, fieldKey)}
        >
          {copied[fieldKey] ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
                <LuArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <FaWhatsapp className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg text-foreground">Configurar WhatsApp</h1>
                <p className="text-xs text-muted-foreground">Múltiples opciones de integración</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-6">

        {/* Token expirado */}
        {config?.token_status === "expired" && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex gap-4 p-5 rounded-2xl bg-red-500/10 border border-red-500/30">
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center shrink-0">
                <LuTriangleAlert className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-red-400">Token de acceso caducado</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Los mensajes de WhatsApp <strong className="text-foreground">no se están enviando</strong>.
                  Tu access token de Meta ha caducado
                  {config.token_status_updated_at && (
                    <> — detectado el {new Date(config.token_status_updated_at).toLocaleString("es-ES")}</>
                  )}.
                  Genera un nuevo token en Meta for Developers y actualiza tus credenciales.
                </p>
                <div className="flex flex-wrap gap-3 mt-3">
                  <a
                    href="https://developers.facebook.com/apps/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    Abrir Meta for Developers <LuExternalLink className="w-3 h-3" />
                  </a>
                  <button
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-red-400 hover:underline"
                    onClick={() => setAddDialogOpen(true)}
                  >
                    <LuRefreshCw className="w-3 h-3" />
                    Actualizar token ahora
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 1: Numbers list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-foreground">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${configs.length > 0 ? 'bg-green-500/20 text-green-400' : 'bg-primary/20 text-primary'}`}>
                    {configs.length > 0 ? <LuCircleCheck className="w-5 h-5" /> : '1'}
                  </span>
                  Números de WhatsApp conectados
                </CardTitle>
                <Dialog open={addDialogOpen} onOpenChange={(open) => {
                    if (!open) { setEditingConfig(null); setFormData(emptyForm); }
                    setAddDialogOpen(open);
                  }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-green-500 hover:bg-green-600 gap-2" size="sm">
                      <LuZap className="w-4 h-4" />Añadir número
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-foreground">
                        {editingConfig ? "Editar número de WhatsApp" : "Añadir número de WhatsApp"}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
                        <p className="text-sm text-foreground">Obtén estas credenciales en <strong>Meta for Developers</strong></p>
                        <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1 mt-1">
                          Abrir Meta for Developers <LuExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Nombre identificativo</Label>
                        <Input placeholder="Ej: Empresa A — Soporte"
                          className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                          value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Phone Number ID</Label>
                        <Input placeholder="Ej: 990827207450847"
                          className="bg-muted/50 border-transparent focus:border-primary rounded-xl font-mono"
                          value={formData.phone_number_id} onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Access Token</Label>
                        <Input type="password" placeholder={editingConfig ? "EAASG... (vacío = no cambiar)" : "EAASG..."}
                          className="bg-muted/50 border-transparent focus:border-primary rounded-xl font-mono"
                          value={formData.access_token} onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                          required={!editingConfig} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">WABA ID <span className="text-muted-foreground font-normal">(para plantillas)</span></Label>
                        <Input placeholder="Ej: 123456789012345"
                          className="bg-muted/50 border-transparent focus:border-primary rounded-xl font-mono"
                          value={formData.waba_id} onChange={(e) => setFormData({ ...formData, waba_id: e.target.value })} />
                        <p className="text-xs text-muted-foreground">Meta for Developers → WhatsApp → API Setup → WhatsApp Business Account ID</p>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="ghost" onClick={() => { setAddDialogOpen(false); setEditingConfig(null); setFormData(emptyForm); }}>Cancelar</Button>
                        <Button type="submit" className="rounded-full bg-green-500 hover:bg-green-600" disabled={saving}>
                          {saving ? "Verificando..." : editingConfig ? "Actualizar" : "Añadir"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {configs.length === 0 ? (
                <p className="text-muted-foreground text-sm">Ningún número conectado. Pulsa <strong>"Añadir número"</strong> para empezar.</p>
              ) : (
                <div className="space-y-3">
                  {configs.map(c => (
                    <div key={c.id} className={`flex items-center gap-4 p-4 rounded-xl border ${c.token_status === "expired" ? "bg-red-500/10 border-red-500/20" : "bg-green-500/10 border-green-500/20"}`}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${c.token_status === "expired" ? "bg-red-500/20" : "bg-green-500/20"}`}>
                        <FaWhatsapp className={`w-6 h-6 ${c.token_status === "expired" ? "text-red-400" : "text-green-500"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{c.name || c.business_name || "WhatsApp Business"}</p>
                        <p className="text-sm text-muted-foreground">{c.display_phone || c.phone_number_id}</p>
                        <p className="text-xs text-muted-foreground font-mono truncate mt-0.5">Webhook: {c.webhook_url}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {c.token_status === "expired" ? (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs"><LuTriangleAlert className="w-3 h-3 mr-1" />Token caducado</Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs"><LuCircleCheck className="w-3 h-3 mr-1" />Activo</Badge>
                        )}
                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" onClick={() => openEdit(c)}>
                          <LuRefreshCw className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => handleDelete(c.id)}>
                          <LuTrash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Step 2: Integration Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-foreground">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
                  2
                </span>
                Método de Integración
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto bg-muted/50 p-1 rounded-xl gap-1">
                  <TabsTrigger value="webhook" className="rounded-lg data-[state=active]:bg-card text-xs sm:text-sm px-2 py-2 whitespace-normal">
                    <LuWebhook className="w-4 h-4 mr-1.5 shrink-0" />
                    Webhook Directo
                  </TabsTrigger>
                  <TabsTrigger value="make" className="rounded-lg data-[state=active]:bg-card text-xs sm:text-sm px-2 py-2 whitespace-normal">
                    <LuZap className="w-4 h-4 mr-1.5 shrink-0" />
                    Make.com
                  </TabsTrigger>
                  <TabsTrigger value="zapier" className="rounded-lg data-[state=active]:bg-card text-xs sm:text-sm px-2 py-2 whitespace-normal">
                    <LuCloud className="w-4 h-4 mr-1.5 shrink-0" />
                    Zapier / n8n
                  </TabsTrigger>
                  <TabsTrigger value="api" className="rounded-lg data-[state=active]:bg-card text-xs sm:text-sm px-2 py-2 whitespace-normal">
                    <LuCode className="w-4 h-4 mr-1.5 shrink-0" />
                    API Directa
                  </TabsTrigger>
                </TabsList>

                {/* Webhook Directo */}
                <TabsContent value="webhook" className="mt-6 space-y-4">
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-start gap-3">
                      <LuCircleAlert className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Nota sobre este entorno</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          El entorno local puede tener restricciones de red.
                          Si Meta no puede verificar el webhook, usa Make.com o despliega en tu propio dominio.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <CopyField
                      label="Callback URL (para Meta)"
                      value={webhookUrl}
                      fieldKey="webhook_url"
                    />
                    <CopyField
                      label="Verify Token"
                      value={verifyToken}
                      fieldKey="verify_token"
                    />
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="instructions" className="border-border">
                      <AccordionTrigger className="text-foreground hover:no-underline">
                        Instrucciones paso a paso
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground space-y-3">
                        <p>1. Ve a <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Meta for Developers</a></p>
                        <p>2. Selecciona tu App → WhatsApp → Configuration</p>
                        <p>3. En la sección "Webhook", haz clic en <strong>Edit</strong></p>
                        <p>4. Ingresa la <strong>Callback URL</strong> y <strong>Verify Token</strong> de arriba</p>
                        <p>5. Haz clic en <strong>Verify and Save</strong></p>
                        <p>6. Suscríbete al campo <strong>messages</strong></p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>

                {/* Make.com */}
                <TabsContent value="make" className="mt-6 space-y-4">
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                    <div className="flex items-start gap-3">
                      <LuCircleCheck className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Recomendado</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Make.com es la opción más confiable. Actúa como intermediario entre Meta y BotNexus.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <CopyField
                      label="URL para HTTP Request"
                      value={incomingUrl}
                      fieldKey="make_url"
                    />

                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Body JSON</Label>
                      <div className="flex gap-2">
                        <Input
                          value='{"from": "{{1.messages[].from}}", "message": "{{1.messages[].text.body}}"}'
                          readOnly
                          className="bg-muted/50 border-transparent font-mono text-xs"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="shrink-0"
                          onClick={() => copyToClipboard('{"from": "{{1.messages[].from}}", "message": "{{1.messages[].text.body}}"}', "make_body")}
                        >
                          {copied.make_body ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="instructions" className="border-border">
                      <AccordionTrigger className="text-foreground hover:no-underline">
                        Instrucciones para Make.com
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground space-y-3">
                        <p>1. Crea una cuenta en <a href="https://www.make.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Make.com</a></p>
                        <p>2. Crea un nuevo escenario</p>
                        <p>3. Añade el módulo <strong>WhatsApp Business Cloud → Watch Events</strong></p>
                        <p>4. Conecta tu cuenta de WhatsApp Business</p>
                        <p>5. Añade el módulo <strong>HTTP → Make a Request</strong></p>
                        <p>6. Configura: URL (de arriba), Method: POST, Body type: Raw JSON</p>
                        <p>7. En Request content, pega el Body JSON de arriba</p>
                        <p>8. Guarda y activa el escenario</p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <a href="https://www.make.com/" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="w-full border-primary/30 text-primary hover:bg-primary/10">
                      Ir a Make.com <LuExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </TabsContent>

                {/* Zapier / n8n */}
                <TabsContent value="zapier" className="mt-6 space-y-4">
                  <div className="space-y-4">
                    <CopyField
                      label="Webhook URL"
                      value={incomingUrl}
                      fieldKey="zapier_url"
                    />
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="zapier" className="border-border">
                      <AccordionTrigger className="text-foreground hover:no-underline">
                        Configurar con Zapier
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground space-y-3">
                        <p>1. Crea un Zap con trigger <strong>Webhooks by Zapier → Catch Hook</strong></p>
                        <p>2. Copia la URL del webhook de Zapier</p>
                        <p>3. Configura esa URL como webhook en Meta</p>
                        <p>4. Añade acción <strong>Webhooks by Zapier → POST</strong></p>
                        <p>5. URL: <code>{incomingUrl}</code></p>
                        <p>6. Data: <code>{`{"from": "phone", "message": "text"}`}</code></p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="n8n" className="border-border">
                      <AccordionTrigger className="text-foreground hover:no-underline">
                        Configurar con n8n
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground space-y-3">
                        <p>1. Crea un workflow con nodo <strong>Webhook</strong></p>
                        <p>2. Copia la URL de producción del webhook</p>
                        <p>3. Configura esa URL como webhook en Meta</p>
                        <p>4. Añade nodo <strong>HTTP Request</strong></p>
                        <p>5. Method: POST, URL: <code>{incomingUrl}</code></p>
                        <p>6. Body: JSON con from y message</p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <div className="flex gap-3">
                    <a href="https://zapier.com/" target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full">
                        Zapier <LuExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                    <a href="https://n8n.io/" target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full">
                        n8n <LuExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                </TabsContent>

                {/* API Directa */}
                <TabsContent value="api" className="mt-6 space-y-4">
                  <p className="text-muted-foreground">
                    Para integraciones personalizadas, puedes enviar mensajes directamente a nuestra API.
                  </p>

                  <div className="space-y-4">
                    <CopyField
                      label="Endpoint"
                      value={`POST ${incomingUrl}`}
                      fieldKey="api_endpoint"
                    />
                  </div>

                  <div className="p-4 rounded-xl bg-muted/50 space-y-3">
                    <Label className="text-xs text-muted-foreground">Ejemplo de Request</Label>
                    <pre className="text-sm text-foreground font-mono overflow-x-auto">
                      {`curl -X POST "${incomingUrl}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "34612345678",
    "message": "Hola, necesito información"
  }'`}
                    </pre>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(`curl -X POST "${incomingUrl}" -H "Content-Type: application/json" -d '{"from": "34612345678", "message": "Hola"}'`, "curl")}
                    >
                      {copied.curl ? <LuCheck className="w-4 h-4 mr-2" /> : <LuCopy className="w-4 h-4 mr-2" />}
                      Copiar comando
                    </Button>
                  </div>

                  <div className="p-4 rounded-xl bg-muted/50 space-y-3">
                    <Label className="text-xs text-muted-foreground">Campos del Body</Label>
                    <div className="text-sm space-y-2">
                      <p><code className="bg-card px-2 py-0.5 rounded">from</code> <span className="text-muted-foreground">- Número de teléfono del cliente (sin +)</span></p>
                      <p><code className="bg-card px-2 py-0.5 rounded">message</code> <span className="text-muted-foreground">- Texto del mensaje</span></p>
                      <p><code className="bg-card px-2 py-0.5 rounded">name</code> <span className="text-muted-foreground">- (Opcional) Nombre del contacto</span></p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>

        {/* Step 3: Assignments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-foreground">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
                  3
                </span>
                Asignar Agentes a Números
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Configura qué agente responde a cada número o grupo de números.
              </p>
              <Link to="/phone-assignments">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Configurar Asignaciones
                  <LuArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>

        {/* Step 4: Plantillas (ahora en su propia página) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-foreground">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
                  4
                </span>
                Plantillas de mensajes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Crea, sincroniza y envía plantillas de WhatsApp aprobadas por Meta.
              </p>
              <Link to="/templates">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Ir a Plantillas
                  <LuArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>

        {/* Test */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass-card border-green-500/20 bg-green-500/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center shrink-0">
                  <FaWhatsapp className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-1">Probar la integración</h4>
                  <p className="text-sm text-muted-foreground">
                    Una vez configurado, envía un mensaje a tu WhatsApp Business desde otro teléfono.
                    El agente asignado responderá automáticamente.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

      </main>
    </div>
  );
};

export default WhatsAppSetupPage;
